/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactDatePicker from "react-datepicker";
import Input from "../../Builder/nodes/Logic/Inputs/Input";
import MyPagination from "../../Common/MyPagination";
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { Form } from "react-bootstrap";
import NoDataPlaceholder from "../../Common/NoDataPlaceholder";

export default function Timeline({
  title,
  loader,
  processes,
  total,
  link,
  initialStatus = "",
  useLocalTimeFilters = false,
  liveMonitoringDisabled = false,
  defaultLiveMonitoring = false,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localTimeFilterParams, setLocalTimeFilterParams] = useState(
    () => new URLSearchParams()
  );
  const [status, setStatus] = useState(initialStatus);
  const [liveMonitoring, setLiveMonitoring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetPagination, setResetPagination] = useState(Date.now());

  const zoomLevels = [
    { label: "1 week", time: 7 * 24 * 3600000 },
    { label: "2 days", time: 48 * 3600000 },
    { label: "24 hours", time: 24 * 3600000 },
    { label: "8 hours", time: 8 * 3600000 },
    { label: "1 hour", time: 3600000 },
    { label: "10 min", time: 600000 },
  ];
  const timeFilterParams = useLocalTimeFilters
    ? localTimeFilterParams
    : searchParams;
  const setTimeFilterParams = (params) => {
    if (useLocalTimeFilters) {
      setLocalTimeFilterParams(new URLSearchParams(params));
      return;
    }

    setSearchParams(params);
  };
  const zoom = Number(timeFilterParams.get("zoom") || 4);
  const zoomLevel = zoomLevels[zoom];
  const endAt = timeFilterParams.get("end_at")
    ? new Date(timeFilterParams.get("end_at"))
    : new Date();
  const startAt = timeFilterParams.get("start_at")
    ? new Date(timeFilterParams.get("start_at"))
    : new Date(endAt.getTime() - zoomLevel.time);

  const startAtTimestamp = new Date(startAt).getTime();
  const endAtTimestamp = new Date(endAt).getTime();
  const panAmount = (endAtTimestamp - startAtTimestamp) / 10;
  const scale = 100 / (endAtTimestamp - startAtTimestamp);
  const progressHeight = 30;

  // load processes
  useEffect(() => {
    setResetPagination(Date.now());
    // loader(startAt, endAt, offset, status);
  }, [timeFilterParams, status]);

  useEffect(() => {
    if (liveMonitoringDisabled) {
      setLiveMonitoring(false);
      return;
    }

    if (defaultLiveMonitoring) {
      setLiveMonitoring(true);
    }
  }, [defaultLiveMonitoring, liveMonitoringDisabled]);

  useEffect(() => {
    if (!liveMonitoring) return undefined;

    const refreshInterval = setInterval(() => {
      setTimeFilterParams({
        end_at: new Date().toISOString(),
        ...(timeFilterParams.get("start_at")
          ? { start_at: timeFilterParams.get("start_at") }
          : {}),
        ...(timeFilterParams.get("zoom") ? { zoom } : {}),
      });
    }, 10000);

    return () => clearInterval(refreshInterval);
  }, [liveMonitoring, timeFilterParams, zoom]);

  const loadPage = async (page, perPage) => {
    setLoading(true);
    try {
      await loader(startAt, endAt, status, page * perPage, perPage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="row justify-content-between align-items-end g-2 bg-colored-reverse p-3 mb-3 rounded-3 mx-0">
        <div className="col-auto d-flex align-items-center">
          <div className="fs-4 text-white fw-bold">{title}</div>
          <button
            type="button"
            title="refresh"
            className="btn btn-link p-0 ms-3"
            onClick={() => setResetPagination(Date.now())}
          >
            <FontAwesomeIcon className="text-white" icon={faRotateLeft} />
          </button>
          <div className="div d-inline-block ms-3">
            <span className="fw-bold text-white">Status:</span>
            <Input
              inputType="select"
              value={[status]}
              onChange={(values) => setStatus(values[0])}
              options={[
                { value: "", label: "Show all processes" },
                { value: "active", label: "Show active processes" },
                { value: "inactive", label: "Show inactive processes" },
              ]}
            />
          </div>
          <div
            className="d-inline-block ms-3 text-white fw-bold"
            title="Refreshes every 10 seconds"
          >
            <Form.Check
              type="switch"
              id="timeline-live-monitoring"
              label="Live monitoring"
              checked={liveMonitoring}
              disabled={liveMonitoringDisabled}
              onChange={(event) => setLiveMonitoring(event.target.checked)}
            />
          </div>
        </div>
        <div className="col-12 col-lg-auto ">
          <div className="row g-2 justify-content-center">
            <div className="col-auto d-flex">
              <button
                title="Pan view by 50%"
                type="button"
                className="btn btn-link border-0 ms-1"
                onClick={() =>
                  setTimeFilterParams({
                    end_at: new Date(
                      endAtTimestamp - panAmount * 5
                    ).toISOString(),
                    ...(timeFilterParams.get("start_at")
                      ? {
                          start_at: new Date(
                            startAtTimestamp - panAmount * 5
                          ).toISOString(),
                        }
                      : {}),
                    ...(timeFilterParams.get("zoom") ? { zoom } : {}),
                  })
                }
              >
                <FontAwesomeIcon className="text-white" icon={faAnglesLeft} />
              </button>
              <button
                title="Pan view by 10%"
                type="button"
                className="btn btn-link ms-1 border-0"
                onClick={() =>
                  setTimeFilterParams({
                    end_at: new Date(endAtTimestamp - panAmount).toISOString(),
                    ...(timeFilterParams.get("start_at")
                      ? {
                          start_at: new Date(
                            startAtTimestamp - panAmount
                          ).toISOString(),
                        }
                      : {}),
                    ...(timeFilterParams.get("zoom") ? { zoom } : {}),
                  })
                }
              >
                <FontAwesomeIcon className="text-white" icon={faAngleLeft} />
              </button>
              <ReactDatePicker
                className="form-control border border-primary-subtle text-secondary ms-1"
                showTimeSelect
                selected={startAt}
                dateFormat="yyyy-MM-dd HH:mm:ss"
                onChange={(date) =>
                  // when start date is custom, remove zoom
                  setTimeFilterParams({
                    start_at: date.toISOString(),
                    end_at: endAt.toISOString(),
                  })
                }
              />
            </div>
            <div className="col-auto d-flex">
              <div className="input-group">
                <button
                  type="button"
                  className="btn btn-link border-primary-subtle"
                  disabled={zoom === 0}
                  onClick={() => {
                    if (zoom > 0) {
                      setTimeFilterParams({
                        end_at: endAt.toISOString(),
                        zoom: zoom - 1,
                      });
                    }
                  }}
                >
                  <FontAwesomeIcon
                    className="text-white"
                    icon={faMagnifyingGlassMinus}
                  />
                </button>

                <select
                  className="form-select border-primary-subtle text-secondary"
                  value={timeFilterParams.has("start_at") ? "custom" : zoom}
                  onChange={(e) =>
                    setTimeFilterParams({
                      end_at: endAt.toISOString(),
                      zoom: e.target.value,
                    })
                  }
                >
                  <option value="custom" hidden>
                    Custom range
                  </option>
                  {zoomLevels.map((z, i) => (
                    <option key={i} value={i}>
                      {z.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="btn btn-link border-primary-subtle"
                  disabled={zoom === 5}
                  onClick={() => {
                    if (zoom < 5) {
                      setTimeFilterParams({
                        end_at: endAt.toISOString(),
                        zoom: zoom + 1,
                      });
                    }
                  }}
                >
                  <FontAwesomeIcon
                    className="text-white"
                    icon={faMagnifyingGlassPlus}
                  />
                </button>
              </div>
            </div>
            <div className="col-auto d-flex">
              <ReactDatePicker
                className="form-control border border-secondary-subtle text-secondary"
                showTimeSelect
                selected={endAt}
                dateFormat="yyyy-MM-dd HH:mm:ss"
                onChange={(date) =>
                  setTimeFilterParams({
                    end_at: date.toISOString(),
                    ...(timeFilterParams.get("start_at")
                      ? { start_at: timeFilterParams.get("start_at") }
                      : {}),
                    ...(timeFilterParams.get("zoom") ? { zoom } : {}),
                  })
                }
              />
              <button
                title="Pan view by 10%"
                type="button"
                className="btn btn-link ms-1 border-0"
                onClick={() =>
                  setTimeFilterParams({
                    end_at: new Date(endAtTimestamp + panAmount).toISOString(),
                    ...(timeFilterParams.get("start_at")
                      ? {
                          start_at: new Date(
                            startAtTimestamp + panAmount
                          ).toISOString(),
                        }
                      : {}),
                    ...(timeFilterParams.get("zoom") ? { zoom } : {}),
                  })
                }
              >
                <FontAwesomeIcon className="text-white" icon={faAngleRight} />
              </button>
              <button
                type="button"
                title="Pan view by 50%"
                className="btn btn-link ms-1 border-0"
                onClick={() =>
                  setTimeFilterParams({
                    end_at: new Date(
                      endAtTimestamp + panAmount * 5
                    ).toISOString(),
                    ...(timeFilterParams.get("start_at")
                      ? {
                          start_at: new Date(
                            startAtTimestamp + panAmount * 5
                          ).toISOString(),
                        }
                      : {}),
                    ...(timeFilterParams.get("zoom") ? { zoom } : {}),
                  })
                }
              >
                <FontAwesomeIcon className="text-white" icon={faAnglesRight} />
              </button>
              <button
                type="button"
                title="Go to now"
                className="btn btn-link ms-1 border-0"
                onClick={() =>
                  setTimeFilterParams({
                    end_at: new Date().toISOString(),
                    ...(timeFilterParams.get("start_at")
                      ? {
                          start_at: new Date(
                            startAtTimestamp + panAmount * 5
                          ).toISOString(),
                        }
                      : {}),
                    ...(timeFilterParams.get("zoom") ? { zoom } : {}),
                  })
                }
              >
                <FontAwesomeIcon className="text-white" icon={faClock} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white p-3 mb-5 rounded-3 position-relative">
        {loading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded-3"
            style={{ zIndex: 1 }}
          >
            <div className="spinner-border text-colored" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        <table className="table table-borderless">
          <tbody>
            {processes.map((p) => (
              <React.Fragment key={p.ID}>
                <tr>
                  <td>
                    <div
                      style={{ minWidth: `${80}px` }}
                      className="text-secondary small bg-transparent"
                    >
                      {" "}
                      Status{" "}
                    </div>
                    {p.IsActive ? (
                      <div className="badge text-bg-success">Active</div>
                    ) : (
                      <div className="badge text-bg-secondary">Completed</div>
                    )}
                  </td>
                  {link(p, () => setResetPagination(Date.now()))}
                  <td>
                    <div className="text-secondary small bg-transparent">
                      {" "}
                      Start{" "}
                    </div>
                    <span className="bg-transparent">
                      {new Date(p.StartAt).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    {p.IsActive ? (
                      <>
                        <div className="text-secondary small bg-transparent">
                          Maximum milestone
                        </div>
                        <span className="bg-transparent">
                          {new Date(p.MaxMilestone.Time).toLocaleString()}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="text-secondary small bg-transparent">
                          End
                        </div>
                        <span className="bg-transparent">
                          {new Date(p.EndAt.Time).toLocaleString()}
                        </span>
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <td colSpan={6}>
                    <div
                      className="progress-stacked mt-1 bg-tertiary "
                      style={{ height: progressHeight }}
                    >
                      <div
                        className="progress"
                        role="progressbar"
                        style={{
                          height: progressHeight,
                          width: `${
                            (Math.max(
                              new Date(p.StartAt).getTime(),
                              startAtTimestamp
                            ) -
                              startAtTimestamp) *
                            scale
                          }%`,
                        }}
                      >
                        <div className="progress-bar bg-transparent" />
                      </div>
                      <div
                        className="progress"
                        role="progressbar"
                        style={{
                          height: progressHeight,
                          width: `max(${
                            (Math.max(
                              startAtTimestamp,
                              Math.min(
                                p.IsActive
                                  ? new Date().getTime()
                                  : new Date(p.EndAt.Time).getTime(),
                                endAtTimestamp
                              )
                            ) -
                              Math.min(
                                endAtTimestamp,
                                Math.max(
                                  new Date(p.StartAt).getTime(),
                                  startAtTimestamp
                                )
                              )) *
                            scale
                          }%, 3px)`,
                        }}
                      >
                        <div
                          title={`Start: ${new Date(
                            p.StartAt
                          ).toLocaleString()} End: ${new Date(
                            p.IsActive ? p.MaxMilestone.Time : p.EndAt.Time
                          ).toLocaleString()} `}
                          className={`progress-bar ${
                            p.IsActive ? "bg-success" : "bg-secondary"
                          }`}
                        />
                      </div>
                      {p.IsActive && (
                        <div
                          className="progress"
                          role="progressbar"
                          style={{
                            height: progressHeight,
                            width: `${
                              (Math.max(
                                startAtTimestamp,
                                Math.min(
                                  new Date(p.MaxMilestone.Time).getTime(),
                                  endAtTimestamp
                                )
                              ) -
                                Math.min(
                                  endAtTimestamp,
                                  Math.max(
                                    new Date().getTime(),
                                    startAtTimestamp
                                  )
                                )) *
                              scale
                            }%`,
                          }}
                        >
                          <div
                            title={`Start: ${new Date(
                              p.StartAt
                            ).toLocaleString()} End: ${new Date(
                              p.MaxMilestone.Time
                            ).toLocaleString()} `}
                            className="progress-bar bg-success bg-opacity-50"
                          />
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colSpan={6} className="py-3">
                    <hr className="text-secondary" />
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <MyPagination
          total={total}
          onPaginate={loadPage}
          key={resetPagination}
        />
        {processes.length === 0 && <NoDataPlaceholder />}
      </div>
    </div>
  );
}
